import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isAgentRoute } from './agentRoute';
import { gtagQueue } from '@/lib/gtag';

// Réexporté pour le test : la frontière de la page agent est définie une
// seule fois, dans `agentRoute.ts`, et partagée avec `LinkedInInsightTag`.
export { isAgentRoute };

/**
 * LE TAG GOOGLE ADS DE L'AGENT — AW-17976349261, et de lui seul.
 *
 * LA DEMANDE. Le compte Google Ads demande le snippet gtag.js « posé sur la
 * page /agent » : c'est la destination des campagnes, et le tag y installe le
 * conversion linker (la chaîne GCLID qui rattache une conversion à la clic)
 * et les listes de remarketing du compte.
 *
 * POURQUOI UN COMPOSANT, ET PAS LE SNIPPET DANS index.html. Le snippet
 * officiel se colle dans le <head> — mais `index.html` est LE document de tout
 * le site : le tag y serait posé sur chaque page, prérendues comprises, et
 * non sur /agent. La route /agent est d'ailleurs protégée (enfant d'OsShell
 * sous `ProtectedRoute`) : elle n'a pas de version prérendue, donc le tag
 * n'a de sens qu'au navigateur, au moment où la route est effectivement
 * montée. D'où ce composant, monté au-dessus des routes, qui lit
 * `useLocation` et n'injecte le script QUE sur /agent et ses enfants
 * (`/agent/scheduled/…`). Le patron est celui d'`IntercomProvider`, qui
 * cernait exactement le même problème avec sa liste `MESSENGER_ROUTES` —
 * liste BLANCHE en préfixe plutôt que noire en exclusion, pour qu'une route
 * publique ajoutée demain n'hérite de rien.
 *
 * LE CONSENTEMENT, DÉJÀ TRANCHÉ PAR index.html — C'EST LE CŒUR DU SUJET. Le
 * head pose les défauts du Consent Mode (ad_storage, ad_user_data,
 * ad_personalization, analytics_storage : `denied`) AVANT tout script, et la
 * file `dataLayer` est PARTAGÉE entre GTM et ce gtag.js : chargé après, il
 * relit ces défauts. Sans accord, il n'écrit donc aucun cookie et n'émet que
 * des pings sans cookie — le mode le plus fermé que Google propose, et
 * c'est celui-là qu'on veut : le bandeau n'accorde jamais `ad_storage`
 * (`lib/analytics.ts` le refuse en toutes circonstances), ce tag restera donc
 * en mesure modelée tant qu'un jour éventuel une catégorie `marketing`
 * n'existe dans le bandeau. La promesse de `/privacy` — « les cookies non
 * essentiels ne sont déposés qu'avec votre accord » — reste tenue.
 *
 * UNE FOIS PAR SESSION. Un script ne se décharge pas : quitter /agent
 * n'enlève pas gtag.js, et le réinjecter à chaque navigation ne ferait que
 * dépiler la file en double. Le chargement est donc idempotent — un seul
 * <script>, une seule paire `js`/`config` — même après dix allers-retours
 * home ↔ agent. C'est la même asymétrie que documente
 * `ContentsquareProvider` : on sait démarrer, pas revenir en arrière.
 *
 * En production seulement — sauf demande explicite, sur le patron
 * d'`ENABLED` dans `ContentsquareProvider` : sans barrière, chaque
 * `npm run dev` et chaque aperçu enverrait des conversions de test dans le
 * compte, mêlées aux vraies sans moyen de les distinguer.
 * `VITE_GOOGLE_ADS_DEV=true` lève la barrière pour vérifier le tag en local.
 */

/** Compte Google Ads aikka. Public par nature, comme l'app id Intercom. */
const GOOGLE_ADS_ID = 'AW-17976349261';

/** Le loader gtag.js — l'URL exacte du snippet officiel Google Ads. */
const GTAG_SRC = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;

/** Production, ou dev avec la barrière levée. */
export function googleAdsEnabled(): boolean {
  return import.meta.env.PROD || import.meta.env.VITE_GOOGLE_ADS_DEV === 'true';
}

/**
 * Pose le tag — une seule fois par document.
 *
 * L'ordre du snippet officiel est conservé : le <script> (asynchrone) d'abord,
 * puis `gtag('js')` et `gtag('config')`, qui attendent dans `dataLayer`
 * pendant que le loader descend. Le second appel — même après une navigation
 * — repart dès le `querySelector` : le script est déjà là, la file est déjà
 * servie, et il n'y a rien à refaire.
 */
function loadGoogleAdsTag(): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector('script[data-google-ads-tag]')) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = GTAG_SRC;
  script.dataset.googleAdsTag = GOOGLE_ADS_ID;
  document.head.appendChild(script);

  const push = gtagQueue();
  if (!push) return;
  push('js', new Date());
  push('config', GOOGLE_ADS_ID);
}

/**
 * Le tag Google Ads de la page agent.
 *
 * À placer au-dessus des routes dans `App.tsx`, sous le routeur : c'est en
 * ARRIVANT sur `/agent` — premier chargement comme navigation cliente —
 * qu'il s'installe. Ne rend rien.
 */
export function GoogleAdsAgentTag() {
  const location = useLocation();

  useEffect(() => {
    if (!googleAdsEnabled()) return;
    if (!isAgentRoute(location.pathname)) return;
    loadGoogleAdsTag();
  }, [location.pathname]);

  return null;
}
